import { FileUpload, OnFileDrop } from '../../../components/FileUpload/FileUpload';
import { Button, Col, FormText, ListGroup, ListGroupItem, Row } from 'reactstrap';
import * as React from 'react';
import { Dispatch, SetStateAction, useContext } from 'react';
import { FaTimes } from 'react-icons/fa';
import Select from 'react-select';
import { disabledSelectStylesWithMenu } from '../../../utils/StylingUtils';
import {
  DomainContext,
  FILE_DOCUMENT_TYPE,
  IDocumentType,
  INCIDENT_DOCUMENT_TYPE,
  NOTE_DOCUMENT_TYPE,
  WORKSHOP_DOCUMENT_TYPE,
} from '../../../contexts/DomainContext';
import { useWindowDimensions } from '../../../hooks/WindowHooks';
import { IDocument, IDocumentToUpload } from '../../../components/Files/Documents/Documents';
import LoadingService from '../../../components/Loading/LoadingService';
import * as FileUploadService from '../../../components/FileUpload/FileUploadService';
import * as ToastService from '../../../services/ToastService';

interface IProps {
  docsToUpload: IDocumentToUpload[];
  setDocsToUpload: Dispatch<SetStateAction<IDocumentToUpload[]>>;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  setDocuments: Dispatch<SetStateAction<IDocument[]>>;
  newDocumentPath: string;
  forClient?: boolean;
  forWorkshop?: boolean;
  uploadComplete?: (doc: IDocumentToUpload) => void;
}
export const DocumentStaging: React.FC<IProps> = (props) => {
  const domainContext = useContext(DomainContext);
  const documentTypes = domainContext.fileAndClientDocumentTypes;
  const { xs, sm } = useWindowDimensions();
  const mobile = xs || sm;
  const newDocumentPath = props.newDocumentPath;

  const handleFileDrop: OnFileDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      props.setIsDirty(true);
      props.setDocsToUpload((docs) => [
        ...docs,
        ...acceptedFiles.map(
          (t) =>
            ({
              file: t,
              documentTypeId: !props.forWorkshop && !props.forClient ? FILE_DOCUMENT_TYPE : undefined,
            } as IDocumentToUpload)
        ),
      ]);
    }
  };

  const uploadStagedDocuments = (documents: IDocumentToUpload[]) => {
    if (!newDocumentPath) {
      console.log('Nowhere to upload these... bailing!', documents);
      return;
    }

    const loadingKey: string = LoadingService.add();

    const doUploadOneStagedDocument = (document: IDocumentToUpload, forWorkshop?: boolean) => {
      return FileUploadService.uploadFile(
        document.file,
        !forWorkshop ? newDocumentPath + '/' + document.documentTypeId : newDocumentPath
      ).then((response: any) => {
        if (response.data.status === 'OK') {
          props.setDocuments((docs: IDocument[]) => [...docs, response.data.document as IDocument]);
          props.uploadComplete?.(document);
        }
      });
    };

    const sequenceUploads = Array.from(documents).reduce((p, document: IDocumentToUpload) => {
      return p.then(() =>
        doUploadOneStagedDocument(document, props.forWorkshop).catch((err) => console.log('Upload failed.', err))
      );
    }, Promise.resolve());

    sequenceUploads
      .then(() => {
        console.log('Uploaded all files.');
        LoadingService.remove(loadingKey);
        props.setDocsToUpload([]);
        props.setIsDirty(false);
      })
      .catch((error) => {
        console.log('One or more upload failed.', error);
        LoadingService.remove(loadingKey);
      });
  };

  const save = () => {
    if (!props.docsToUpload.some((d) => !d.documentTypeId) || props.forWorkshop) {
      uploadStagedDocuments(props.docsToUpload);
    } else {
      ToastService.notifyError('All Documents must have a Document Type');
    }
  };

  return (
    <>
      <FileUpload isUploading={false} onDrop={handleFileDrop} uploadSuccessful={false} />
      {props.docsToUpload.length > 0 && (
        <ListGroup className={'my-3'}>
          {props.docsToUpload.map((d, index) => (
            <ListGroupItem className={'py-4'} key={index}>
              <div className={'d-flex'}>
                <div className={'flex-grow-1'}>
                  <Row className='row--condensed align-items-center'>
                    <Col md={3}>
                      <span style={{ wordBreak: 'break-all', textAlign: 'left' }}>{d.file.name}</span>
                    </Col>
                    {!props.forWorkshop && (
                      <Col sm={12} md={3} style={!mobile ? { flexGrow: 1.5, flexShrink: 1, flexBasis: 0 } : {}}>
                        <Select
                          key={`documentTypeId-${index}-${d.documentTypeId}`}
                          name={'documentTypeId'}
                          styles={disabledSelectStylesWithMenu}
                          options={documentTypes.filter(
                            (dt) =>
                              dt.documentTypeId !== NOTE_DOCUMENT_TYPE &&
                              dt.documentTypeId !== WORKSHOP_DOCUMENT_TYPE &&
                              dt.documentTypeId !== INCIDENT_DOCUMENT_TYPE
                          )}
                          placeholder={<span className='placeholder'>Select Document Type...</span>}
                          value={documentTypes.find(
                            (docType: IDocumentType) => docType.documentTypeId === d?.documentTypeId
                          )}
                          onChange={(value: any) => {
                            props.setDocsToUpload((oldD) => [
                              ...oldD.slice(0, index),
                              { ...oldD[index], documentTypeId: value ? value.documentTypeId : undefined },
                              ...oldD.slice(index + 1),
                            ]);
                          }}
                          getOptionValue={(option: IDocumentType) => option.documentTypeId}
                          getOptionLabel={(option: IDocumentType) => option.name}
                        />
                      </Col>
                    )}
                    <Col md={'auto'}>
                      <div className={'d-none d-md-block'} style={{ width: 42 }}>
                        <Button
                          color={'link'}
                          className={'ml-auto p-0 text-danger'}
                          onClick={() => {
                            props.setDocsToUpload((oldD) => [...oldD.slice(0, index), ...oldD.slice(index + 1)]);
                          }}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </ListGroupItem>
          ))}
          <ListGroupItem className={'d-flex justify-content-center align-items-center flex-column'}>
            <Button color={'primary'} className={'mt-3'} onClick={save} disabled={props.docsToUpload.length === 0}>
              Upload Documents
            </Button>
            <FormText color='muted' className={'my-1'}>
              New documents will be uploaded when changes are saved.
            </FormText>
          </ListGroupItem>
        </ListGroup>
      )}
    </>
  );
};
